import { Alert, Card, DatePicker, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';

interface AgencyRow {
  rank: number;
  groupId: number | null;
  name: string;
  tiktokGroupName: string;
  totalDiamond: string;
  creatorCount: number;
  asOf: string;
  manager: { name: string; username: string | null } | null;
  creators: { uid: string; tiktokId: string | null; diamonds: number }[];
}

export default function AgencyRanking() {
  const [month, setMonth] = useState(dayjs());
  const [rows, setRows] = useState<AgencyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    AxiosUtils.get(`${API.revenues}/agency-ranking`, { month: month.format('YYYY-MM') })
      .then((res: any) => { if (active) setRows(res?.data || []); })
      .catch(() => { if (active) { setRows([]); setError(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month]);

  return (
    <Card title="BXH Agency · tự tính từ file TikTok" style={{ marginBottom: 16 }}>
      <Space wrap style={{ marginBottom: 12 }}>
        <DatePicker picker="month" allowClear={false} value={month} format="MM/YYYY"
          onChange={(value) => { if (value) setMonth(value); }} />
        <Typography.Text type="secondary">
          Ghép bằng đúng tên nhóm TikTok trong <Link to="/domestic-management">Cộng đồng → Nhóm quản lý</Link>.
          Chạm vào một dòng để xem Creator.
        </Typography.Text>
      </Space>
      {error && <Alert type="error" showIcon message="Không tải được số liệu Agency" style={{ marginBottom: 12 }} />}
      <Table
        size="small"
        loading={loading}
        rowKey="tiktokGroupName"
        dataSource={rows}
        pagination={{ defaultPageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: 720 }}
        locale={{ emptyText: 'Tháng này chưa có dữ liệu TikTok theo nhóm' }}
        columns={[
          { title: '#', dataIndex: 'rank', width: 55 },
          { title: 'Agency', key: 'name', render: (_, row) => row.groupId
            ? <Link to={`/group/${row.groupId}/management`}>{row.name}</Link>
            : row.name },
          { title: 'Nhóm trong file', dataIndex: 'tiktokGroupName' },
          // Đã ghép nhóm mà chưa có trưởng team (không bắt buộc, bổ sung sau) → để trống, không gắn nhãn "Chưa ghép nhóm".
          { title: 'Trưởng team', key: 'manager', render: (_, row) => row.manager?.name
            || (row.groupId ? '—' : <Tag color="orange">Chưa ghép nhóm</Tag>) },
          { title: 'Creator', dataIndex: 'creatorCount', align: 'right', width: 90 },
          { title: 'Tổng KC', dataIndex: 'totalDiamond', align: 'right', width: 145,
            render: (value: string) => Number(value).toLocaleString('vi-VN') },
        ]}
        expandable={{
          expandedRowRender: (row) => (
            <Table size="small" rowKey="uid" pagination={{ defaultPageSize: 10, hideOnSinglePage: true }}
              dataSource={row.creators} columns={[
                { title: 'TikTok ID', dataIndex: 'tiktokId', render: (name: string | null) => name ? `@${name}` : '—' },
                { title: 'UID TikTok', dataIndex: 'uid' },
                { title: 'Kim cương', dataIndex: 'diamonds', align: 'right', render: (value: number) => value.toLocaleString('vi-VN') },
              ]} />
          ),
        }}
      />
    </Card>
  );
}
